import api from './api';

// Transform a flat AdmissionResponse from the backend into the shape
// that StudentAnalytics.js expects:
//   amenities     → { accommodation: bool, breakfast: bool, ... }
//   installments  → ["2024-01-10", "2024-03-15", ...]  (empty array when none set)
const normalizeAdmission = (data) => {
  if (!data) return data;

  // Build amenities object from flat boolean fields
  const amenities = {
    accommodation: !!data.accommodation,
    breakfast:     !!data.breakfast,
    dinner:        !!data.dinner,
    wifi:          !!data.wifi,
    readingRoom:   !!data.readingRoom,
    transport:     !!data.transport,
    lunch:         !!data.lunch,
    gymnasium:     !!data.gymnasium,
  };

  // Build installments array from inst1..inst4 (skip nulls/blanks)
  // Always return an array (never undefined) so UI can safely check .length
  const installments = [data.inst1, data.inst2, data.inst3, data.inst4]
    .filter(d => d != null && d !== '');

  return {
    ...data,
    amenities,
    installments, // always an array, may be empty []
  };
};

const admissionService = {

  getAll: async (page = 0, size = 10, search = '') => {
    const params = { page, size };
    if (search) params.search = search;
    const r = await api.get('/admissions', { params });
    return r.data;
  },

  getEligibleStudents: async () => {
    const r = await api.get('/admissions/eligible-students');
    return r.data;
  },

  getStats: async () => {
    const r = await api.get('/admissions/stats');
    return r.data;
  },

  getById: async (id) => {
    const r = await api.get(`/admissions/${id}`);
    return normalizeAdmission(r.data);
  },

  allocate: async (data) => {
    const r = await api.post('/admissions/allocate', data);
    return r.data;
  },

  updateRoom: async (id, data) => {
    const r = await api.patch(`/admissions/${id}/room`, data);
    return r.data;
  },

  // Update full admission record (fees, amenities, installments, payment)
  updateFull: async (id, data) => {
    const r = await api.put(`/admissions/${id}`, data);
    return normalizeAdmission(r.data);
  },

  updateStatus: async (id, status, remarks = '') => {
    const r = await api.patch(`/admissions/${id}/status`, { status, remarks });
    return r.data;
  },

  updatePresence: async (id, presence) => {
    const r = await api.patch(`/admissions/${id}/presence`, { presence });
    return r.data;
  },

  updatePoliceVerification: async (id, status) => {
    const r = await api.patch(`/admissions/${id}/police-verification`, { status });
    return r.data;
  },

  remove: async (id) => {
    const r = await api.delete(`/admissions/${id}`);
    return r.data;
  },

  // Get FULL admission record for a specific student (including installments + amenities)
  // Step 1: find admission in list to get its ID
  // Step 2: fetch full record by ID (list endpoint may omit nested fields)
  getByStudentId: async (studentId) => {
    const r = await api.get('/admissions', { params: { page: 0, size: 1000 } });
    const list = r.data?.content || r.data || [];

    // FIX: Use loose equality (==) to handle number vs string type mismatch
    // e.g. studentId=5 (number) must match a.studentId="5" (string) or vice versa
    // eslint-disable-next-line eqeqeq
    const summary = list.find(a => a.studentId == studentId);
    if (!summary) return null;

    // Fetch full record to get installments, amenities, and all nested fields
    try {
      const full = await api.get(`/admissions/${summary.id}`);
      return normalizeAdmission(full.data);
    } catch (e) {
      // Fall back to summary if full fetch fails
      console.warn('Could not fetch full admission record, using summary:', e.message);
      return normalizeAdmission(summary);
    }
  },
};

export default admissionService;